import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import {
  ArrowLeftIcon,
  DocumentTextIcon,
  UserIcon,
  CalendarIcon,
  ClockIcon,
  PrinterIcon,
  EllipsisVerticalIcon,
  BanknotesIcon,
  SignalIcon,
} from '@heroicons/react/24/outline';
import { invoicesAPI, auditLogsAPI, fiscalAPI } from '../services/api';
import {
  Card,
  Button,
  LoadingSpinner,
  StatusBadge,
  PageHeader,
  DataTableCard,
  Table,
  BottomSheet,
} from '../components/ui';
import { Invoice, TableColumn } from '../types';
import { clsx } from 'clsx';
import { usePermissions } from '../hooks/usePermissions';

const formatCurrency = (minor?: number, major?: number, currency = 'USD') => {
  const amount = minor != null ? minor / 100 : Number(major || 0);
  return new Intl.NumberFormat('es-ES', { style: 'currency', currency }).format(amount);
};

const invoiceStatusBadge = (status?: string) => {
  const toneMap: Record<string, 'success' | 'danger' | 'warning' | 'neutral' | 'info'> = {
    DRAFT: 'neutral',
    ISSUED: 'info',
    PARTIAL: 'warning',
    PAID: 'success',
    OVERDUE: 'danger',
    CANCELLED: 'neutral',
    PENDING: 'warning',
  };
  const labelMap: Record<string, string> = {
    DRAFT: 'Borrador',
    ISSUED: 'Emitida',
    PARTIAL: 'Parcial',
    PAID: 'Pagada',
    OVERDUE: 'Vencida',
    CANCELLED: 'Anulada',
    PENDING: 'Pendiente',
  };
  const key = status || 'PENDING';
  return <StatusBadge tone={toneMap[key] || 'warning'}>{labelMap[key] || key}</StatusBadge>;
};

const statusChipClass = (status?: string) => {
  if (status === 'PAID') return 'bg-emerald-50 text-emerald-700 ring-emerald-200';
  if (status === 'OVERDUE') return 'bg-rose-50 text-rose-700 ring-rose-200';
  if (status === 'CANCELLED' || status === 'DRAFT') return 'bg-slate-100 text-slate-600 ring-slate-200';
  if (status === 'ISSUED') return 'bg-indigo-50 text-indigo-700 ring-indigo-200';
  return 'bg-amber-50 text-amber-700 ring-amber-200';
};

const statusLabel = (status?: string) => {
  const labelMap: Record<string, string> = {
    DRAFT: 'Borrador',
    ISSUED: 'Emitida',
    PARTIAL: 'Parcial',
    PAID: 'Pagada',
    OVERDUE: 'Vencida',
    CANCELLED: 'Anulada',
    PENDING: 'Pendiente',
  };
  return labelMap[status || ''] || status || '—';
};

const movementTypeLabel = (type?: string) => {
  const labelMap: Record<string, string> = {
    INVOICE_CHARGE: 'Cargo de factura',
    PAYMENT_CREDIT: 'Crédito de pago',
    PAYMENT_ALLOCATION: 'Aplicación de pago',
    PAYMENT_REVERSAL: 'Reverso de pago',
    REFUND_DEBIT: 'Débito por reembolso',
    CREDIT_NOTE: 'Nota de crédito',
    ADJUSTMENT: 'Ajuste',
  };
  return labelMap[type || ''] || type || 'Movimiento';
};

const InvoiceDetail: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isAdmin } = usePermissions();
  const [moreOpen, setMoreOpen] = useState(false);

  const { data: invoiceResponse, isLoading } = useQuery<{ data: { data: Invoice } }>(
    ['invoice-detail', id],
    () => invoicesAPI.getById(id),
    { enabled: Boolean(id) }
  );

  const { data: auditResponse } = useQuery<{ data: { data: any[] } }>(
    ['invoice-audit', id],
    () =>
      auditLogsAPI.list({
        entityType: 'INVOICE',
        search: String(id),
        limit: 20,
      }),
    { enabled: Boolean(id) }
  );

  const issueEcf = useMutation(
    () => fiscalAPI.issueFromInvoice(Number(id), { ecfType: 'E32' }),
    {
      onSuccess: (res) => {
        const encf = res.data?.data?.encf;
        const stub = res.data?.data?.stub;
        toast.success(
          stub
            ? `e-CF ${encf} emitido (modo stub / pendiente TrackId)`
            : `e-CF ${encf} enviado a DGII`
        );
        queryClient.invalidateQueries(['invoice-detail', id]);
        queryClient.invalidateQueries('fiscal-documents');
        queryClient.invalidateQueries('fiscal-dashboard');
      },
      onError: (e: any) =>
        toast.error(e.response?.data?.message || 'No se pudo emitir e-CF'),
    }
  );

  const invoice = invoiceResponse?.data?.data;
  const allocations = invoice?.paymentAllocations || [];
  const movements = invoice?.receivableMovements || [];
  const audits = auditResponse?.data?.data || [];

  const allocationColumns: TableColumn<any>[] = [
    {
      key: 'payment',
      label: 'Pago',
      render: (_, row) => (
        <div>
          <div className="font-medium text-slate-900">#{row.payment?.id}</div>
          <div className="text-xs text-slate-500">{row.payment?.reference || 'Sin referencia'}</div>
        </div>
      ),
    },
    {
      key: 'amountAppliedMinor',
      label: 'Monto aplicado',
      render: (value) => formatCurrency(value, undefined, invoice?.currency || 'USD'),
    },
    {
      key: 'createdAt',
      label: 'Fecha',
      render: (value) => new Date(value).toLocaleString(),
    },
    {
      key: 'status',
      label: 'Estado pago',
      render: (_, row) => invoiceStatusBadge(row.payment?.status),
    },
  ];

  if (isLoading || !invoice) {
    return (
      <div className="flex h-64 items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const number = invoice.invoiceNumber || `#${invoice.id}`;
  const clientName = invoice.client?.name || 'N/A';
  const balance = formatCurrency(invoice.balanceDueMinor, invoice.amountDue, invoice.currency);
  const total = formatCurrency(invoice.totalMinor, invoice.amountDue, invoice.currency);
  const due = invoice.dueDate
    ? new Date(invoice.dueDate).toLocaleDateString('es', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
    : '—';

  return (
    <div className="space-y-4 animate-fade-in-up pb-8 md:space-y-6">
      {/* Desktop header */}
      <div className="hidden md:block">
        <PageHeader
          title={`Detalle de Factura ${number}`}
          subtitle="Resumen financiero, aplicaciones, timeline y auditoría."
          actions={
            <div className="flex items-center gap-2">
              {isAdmin && (
                <Button
                  variant="outline"
                  onClick={() => issueEcf.mutate()}
                  loading={issueEcf.isLoading}
                  className="border-emerald-300 text-emerald-800 hover:bg-emerald-50"
                >
                  <SignalIcon className="mr-2 h-4 w-4" />
                  Emitir e-CF
                </Button>
              )}
              <Button
                variant="outline"
                onClick={() => navigate(`/invoices/${id}/print`)}
                className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
              >
                <PrinterIcon className="mr-2 h-4 w-4" />
                Imprimir / Plantilla
              </Button>
              <Button variant="outline" onClick={() => navigate('/cobros?tab=facturas')}>
                <ArrowLeftIcon className="mr-2 h-4 w-4" />
                Volver al listado
              </Button>
            </div>
          }
        />
      </div>

      {/* Mobile header + summary */}
      <div className="space-y-4 md:hidden">
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={() => navigate('/cobros?tab=facturas')}
            className="inline-flex h-10 w-10 items-center justify-center rounded-xl text-slate-600 transition hover:bg-slate-100 active:scale-95"
            aria-label="Volver"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </button>
          <button
            type="button"
            onClick={() => setMoreOpen(true)}
            className="inline-flex h-10 w-10 items-center justify-center rounded-xl text-slate-600 transition hover:bg-slate-100 active:scale-95"
            aria-label="Más acciones"
          >
            <EllipsisVerticalIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="text-center">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-primary-50 text-primary-700">
            <DocumentTextIcon className="h-7 w-7" />
          </div>
          <h1 className="mt-3 text-xl font-bold text-slate-900">{number}</h1>
          <div className="mt-2 flex flex-wrap items-center justify-center gap-2">
            <span
              className={clsx(
                'rounded-md px-2 py-0.5 text-[11px] font-semibold ring-1',
                statusChipClass(invoice.status)
              )}
            >
              {statusLabel(invoice.status)}
            </span>
          </div>
          <p className="mt-2 text-sm font-medium text-slate-600">{clientName}</p>
          <p className="text-xs text-slate-500">{invoice.client?.cedula || '—'}</p>
        </div>

        <div className="grid grid-cols-3 gap-2 rounded-2xl border border-[#E5EAF1] bg-white px-2 py-3 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
          <div className="text-center">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Total</p>
            <p className="mt-1 text-sm font-bold tabular-nums text-slate-900">{total}</p>
          </div>
          <div className="border-x border-[#E5EAF1] text-center">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Saldo</p>
            <p className="mt-1 text-sm font-bold tabular-nums text-rose-600">{balance}</p>
          </div>
          <div className="text-center">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Vence</p>
            <p className="mt-1 text-xs font-semibold text-slate-900">{due}</p>
          </div>
        </div>

        <div className="overflow-hidden rounded-2xl border border-[#E5EAF1] bg-white">
          <DetailRow
            label="Plan"
            value={invoice.clientPlan?.planTemplate?.name || 'N/A'}
          />
          <DetailRow
            label="Tipo"
            value={invoice.clientPlan?.planTemplate?.planType || 'N/A'}
          />
          <DetailRow
            label="Subtotal"
            value={formatCurrency(invoice.subtotalMinor, invoice.amountDue, invoice.currency)}
          />
          <DetailRow
            label="Cargos"
            value={formatCurrency(
              (invoice.taxMinor || 0) + (invoice.moraMinor || 0),
              undefined,
              invoice.currency
            )}
          />
          <DetailRow label="Total" value={total} />
          <DetailRow label="Saldo pendiente" value={balance} last />
        </div>

        <div className="flex items-center justify-center gap-6 pt-1">
          <button
            type="button"
            onClick={() =>
              navigate(
                `/cobros?clientId=${invoice.clientId || invoice.client?.id || ''}&tab=pendiente`
              )
            }
            className="flex flex-col items-center gap-1.5 text-xs font-semibold text-slate-600"
          >
            <span className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-[#E5EAF1] bg-white shadow-sm">
              <BanknotesIcon className="h-5 w-5 text-primary-600" />
            </span>
            Cobrar
          </button>
          <button
            type="button"
            onClick={() => navigate(`/invoices/${id}/print`)}
            className="flex flex-col items-center gap-1.5 text-xs font-semibold text-slate-600"
          >
            <span className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-[#E5EAF1] bg-white shadow-sm">
              <PrinterIcon className="h-5 w-5 text-primary-600" />
            </span>
            Imprimir
          </button>
          <button
            type="button"
            onClick={() => setMoreOpen(true)}
            className="flex flex-col items-center gap-1.5 text-xs font-semibold text-slate-600"
          >
            <span className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-[#E5EAF1] bg-white shadow-sm">
              <EllipsisVerticalIcon className="h-5 w-5 text-slate-600" />
            </span>
            Más
          </button>
        </div>
      </div>

      {/* Desktop summary cards */}
      <div className="hidden gap-4 md:grid md:grid-cols-1 lg:grid-cols-3">
        <Card className="p-5">
          <div className="mb-2 flex items-center gap-2 text-sm text-slate-600">
            <DocumentTextIcon className="h-4 w-4" />
            Factura
          </div>
          <div className="text-lg font-semibold text-slate-900">{number}</div>
          <div className="mt-2">{invoiceStatusBadge(invoice.status)}</div>
        </Card>
        <Card className="p-5">
          <div className="mb-2 flex items-center gap-2 text-sm text-slate-600">
            <UserIcon className="h-4 w-4" />
            Cliente
          </div>
          <div className="text-lg font-semibold text-slate-900">{clientName}</div>
          <div className="text-sm text-slate-500">{invoice.client?.cedula || 'N/A'}</div>
        </Card>
        <Card className="p-5">
          <div className="mb-2 flex items-center gap-2 text-sm text-slate-600">
            <CalendarIcon className="h-4 w-4" />
            Plan / contrato
          </div>
          <div className="text-lg font-semibold text-slate-900">
            {invoice.clientPlan?.planTemplate?.name || 'N/A'}
          </div>
          <div className="text-sm text-slate-500">
            {invoice.clientPlan?.planTemplate?.planType || 'N/A'}
          </div>
        </Card>
      </div>

      <div className="hidden gap-4 md:grid md:grid-cols-2 lg:grid-cols-4">
        <Card className="p-5">
          <div className="text-xs uppercase text-slate-500">Subtotal</div>
          <div className="text-xl font-semibold">
            {formatCurrency(invoice.subtotalMinor, invoice.amountDue, invoice.currency)}
          </div>
        </Card>
        <Card className="p-5">
          <div className="text-xs uppercase text-slate-500">Cargos (impuesto + mora)</div>
          <div className="text-xl font-semibold">
            {formatCurrency(
              (invoice.taxMinor || 0) + (invoice.moraMinor || 0),
              undefined,
              invoice.currency
            )}
          </div>
        </Card>
        <Card className="p-5">
          <div className="text-xs uppercase text-slate-500">Total</div>
          <div className="text-xl font-semibold">{total}</div>
        </Card>
        <Card className="border-emerald-200 bg-emerald-50/40 p-5">
          <div className="text-xs uppercase text-emerald-700">Saldo pendiente</div>
          <div className="text-xl font-semibold text-emerald-900">{balance}</div>
        </Card>
      </div>

      {/* Allocations: cards on mobile, table on desktop */}
      <div className="md:hidden">
        <h3 className="mb-2 text-sm font-semibold text-slate-900">Pagos aplicados</h3>
        {allocations.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-200 bg-white px-4 py-8 text-center text-sm text-slate-500">
            Esta factura aún no tiene aplicaciones de pago.
          </div>
        ) : (
          <div className="space-y-2">
            {allocations.map((row: any) => (
              <div
                key={row.id}
                className="rounded-2xl border border-[#E5EAF1] bg-white p-3.5 shadow-[0_1px_2px_rgba(15,23,42,0.04)]"
              >
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="text-sm font-semibold text-slate-900">Pago #{row.payment?.id}</p>
                    <p className="text-xs text-slate-500">{row.payment?.reference || 'Sin referencia'}</p>
                  </div>
                  <p className="text-sm font-bold tabular-nums text-slate-900">
                    {formatCurrency(row.amountAppliedMinor, undefined, invoice.currency)}
                  </p>
                </div>
                <p className="mt-2 text-xs text-slate-500">
                  {row.createdAt ? new Date(row.createdAt).toLocaleString() : '—'}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="hidden md:block">
        <DataTableCard>
          <div className="border-b border-slate-200 px-5 py-4">
            <h3 className="font-semibold text-slate-900">Pagos aplicados</h3>
          </div>
          <Table
            data={allocations}
            columns={allocationColumns}
            emptyMessage="Esta factura aún no tiene aplicaciones de pago."
          />
        </DataTableCard>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card className="p-4 md:p-5">
          <h3 className="mb-3 font-semibold text-slate-900">Timeline de eventos</h3>
          <div className="max-h-80 space-y-3 overflow-auto">
            {movements.length === 0 ? (
              <p className="text-sm text-slate-500">Sin movimientos financieros registrados.</p>
            ) : (
              movements.map((movement: any) => (
                <div
                  key={movement.id}
                  className="rounded-xl border border-slate-200 bg-white p-3"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-semibold text-slate-700">
                      {movementTypeLabel(movement.movementType)}
                    </span>
                    <span className="text-xs text-slate-500">
                      {new Date(movement.eventDate).toLocaleString()}
                    </span>
                  </div>
                  <p className="mt-1 text-sm text-slate-800">{movement.description}</p>
                  <div className="mt-1 text-xs text-slate-500">
                    Debe: {formatCurrency(movement.debitMinor, undefined, invoice.currency)} | Haber:{' '}
                    {formatCurrency(movement.creditMinor, undefined, invoice.currency)}
                  </div>
                </div>
              ))
            )}
          </div>
        </Card>

        <Card className="p-4 md:p-5">
          <h3 className="mb-3 font-semibold text-slate-900">Historial de auditoría</h3>
          <div className="max-h-80 space-y-3 overflow-auto">
            {audits.length === 0 ? (
              <p className="text-sm text-slate-500">
                Sin eventos de auditoría visibles para esta factura.
              </p>
            ) : (
              audits.map((audit: any) => (
                <div key={audit.id} className="rounded-xl border border-slate-200 bg-white p-3">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-semibold text-slate-700">{audit.action}</span>
                    <span className="text-xs text-slate-500">
                      <ClockIcon className="mr-1 inline h-3 w-3" />
                      {new Date(audit.createdAt).toLocaleString()}
                    </span>
                  </div>
                  <p className="mt-1 text-sm text-slate-800">{audit.description}</p>
                  <p className="mt-1 text-xs text-slate-500">
                    Usuario: {audit.user?.name || 'Sistema'}
                  </p>
                </div>
              ))
            )}
          </div>
        </Card>
      </div>

      <BottomSheet open={moreOpen} onClose={() => setMoreOpen(false)} title="Más acciones">
        <div className="space-y-0.5 pb-2">
          <button
            type="button"
            onClick={() => {
              setMoreOpen(false);
              navigate(`/invoices/${id}/print`);
            }}
            className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-sm font-medium text-slate-800 hover:bg-slate-50"
          >
            <PrinterIcon className="h-5 w-5 text-slate-400" />
            Imprimir / Plantilla
          </button>
          <button
            type="button"
            onClick={() => {
              setMoreOpen(false);
              navigate(
                `/cobros?clientId=${invoice.clientId || invoice.client?.id || ''}&tab=pendiente`
              );
            }}
            className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-sm font-medium text-slate-800 hover:bg-slate-50"
          >
            <BanknotesIcon className="h-5 w-5 text-slate-400" />
            Ir a Cobros
          </button>
          <button
            type="button"
            onClick={() => {
              setMoreOpen(false);
              navigate('/cobros?tab=facturas');
            }}
            className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-sm font-medium text-slate-800 hover:bg-slate-50"
          >
            <ArrowLeftIcon className="h-5 w-5 text-slate-400" />
            Volver al listado
          </button>
        </div>
      </BottomSheet>
    </div>
  );
};

const DetailRow: React.FC<{
  label: string;
  value: string;
  last?: boolean;
}> = ({ label, value, last }) => (
  <div
    className={clsx(
      'flex items-start justify-between gap-3 px-4 py-3',
      !last && 'border-b border-[#E5EAF1]'
    )}
  >
    <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</p>
    <p className="text-right text-sm font-medium text-slate-900">{value}</p>
  </div>
);

export default InvoiceDetail;
